"use client"

import { HStack, VStack } from "@chakra-ui/react/stack"
import dynamic from "next/dynamic"
import { type FC } from "react"

import {
  type ContentBlockItem,
  type ContentBlockWrapper,
} from "@/types/database"

import ColorPaletteBlock from "./ColorPaletteBlock"
import ColorPaletteColumnsBlock from "./ColorPaletteColumnsBlock"
import DividerBlock from "./DividerBlock"
import FaqBlock from "./FaqBlock"
import FigmaBlock from "./FigmaBlock"
import FullImageBlock from "./FullImageBlock"
import GalleryBlock from "./GalleryBlock"
import GridCardsBlock from "./GridCardsBlock"
import HtmlBlock from "./HtmlBlock"
import KeyDesignDecisionsBlock from "./KeyDesignDecisionsBlock"
import MarkdownBlock from "./MarkdownBlock"
import MockupBlock from "./MockupBlock"
import OutcomeBlock from "./OutcomeBlock"
import PersonaBlock from "./PersonaBlock"
import ProsAndConsBlock from "./ProsAndConsBlock"
import QuoteBlock from "./QuoteBlock"
import StatsBlock from "./StatsBlock"
import SVGBlock from "./SVGBlock"
import UrlBlock from "./UrlBlock"

// Heavy blocks (swiper, react-compare, recharts/d3, video players) — load on demand
const CarouselBlock = dynamic(() => import("./CarouselBlock"), { ssr: false })
const CompareBlock = dynamic(() => import("./CompareBlock"), { ssr: false })
const DonutChart = dynamic(() => import("./DonutChartBlock"), { ssr: false })
const VideoBlock = dynamic(() => import("./VideoBlock"), { ssr: false })

interface ContentBlockRendererProps {
  className?: string
  contentBlocks: ContentBlockWrapper[]
}

interface ContentBlockProps {
  block: ContentBlockItem
  blockTitleNumber?: number
  fullImageNumber?: number
}

interface NumberedContentBlock {
  block: ContentBlockWrapper
  blockTitleNumber?: number
  fullImageNumber?: number
}

interface NumberedContentBlockState {
  blockTitleCount: number
  fullImageCount: number
  items: NumberedContentBlock[]
}

const getContentBlockTitle = (block: ContentBlockItem) => {
  switch (block.__typename) {
    case "html":
    case "padding":
    case "project_overview":
    case "quote":
      return undefined
    case "outcome":
      return block.title?.trim() || block.headline?.trim() || "Outcome"
    case "persona":
      return block.title?.trim() || block.full_name?.trim()
    case "video":
      return block.title?.trim() || "Video"
    default:
      return "title" in block && typeof block.title === "string"
        ? block.title.trim()
        : undefined
  }
}

const Wrapper = ({
  borderBottom,
  children,
  className,
  showTopDivider,
}: {
  borderBottom?: boolean
  children: React.ReactNode
  className?: string
  showTopDivider: boolean
}) => (
  <VStack
    _first={{
      borderTop: "0",
    }}
    alignItems="flex-start"
    as="section"
    borderBottom={borderBottom ? "1px solid {colors.border}" : undefined}
    borderTop={showTopDivider ? "1px solid {colors.border}" : undefined}
    className={className}
    css={{
      "& > :is(.internal-padding, .edge-to-edge)": {
        borderLeft: "1px solid {colors.border}",
        borderRight: "1px solid {colors.border}",
        maxW: "8xl",
        mx: "auto",
        w: "full",
      },
      "& > .edge-to-edge": {
        py: 0,
      },
      "& > .internal-padding": {
        px: { base: 8, md: 10, lg: 24, xl: 28 },
        pb: { base: 8, md: 10, lg: 24, xl: 28 },
        pt: {
          base: showTopDivider ? 8 : 0,
          md: showTopDivider ? 10 : 0,
          lg: showTopDivider ? 24 : 0,
          xl: showTopDivider ? 40 : 0,
        },
        gap: { base: 4, md: 8, lg: 12 },
        marginTop: {
          base: !showTopDivider ? -6 : undefined,
          md: !showTopDivider ? -6 : undefined,
          lg: !showTopDivider ? -16 : undefined,
        },
      },
    }}
    gap={0}
    mb={0}
    pb={0}
    width="100%"
  >
    {children}
  </VStack>
)

const getShowTopDivider = (block: ContentBlockWrapper, index: number) => {
  const showTopDivider = block.item?.show_top_divider

  if (typeof showTopDivider === "boolean") {
    return showTopDivider
  }

  return index > 0
}

const ContentBlock = ({
  block,
  blockTitleNumber,
  fullImageNumber,
}: ContentBlockProps) => {
  switch (block.__typename) {
    case "markdown":
      return <MarkdownBlock block={block} titleNumber={blockTitleNumber} />
    case "outcome":
      return <OutcomeBlock block={block} titleNumber={blockTitleNumber} />
    case "faq":
      return <FaqBlock block={block} titleNumber={blockTitleNumber} />
    case "gallery":
      return <GalleryBlock block={block} titleNumber={blockTitleNumber} />
    case "grid_cards":
    case "GridCards":
      return <GridCardsBlock block={block} titleNumber={blockTitleNumber} />
    case "stats":
    case "stats_block":
    case "StatsBlock":
      return <StatsBlock block={block} titleNumber={blockTitleNumber} />
    case "key_design_decisions":
    case "KeyDesignDecisions":
      return (
        <KeyDesignDecisionsBlock block={block} titleNumber={blockTitleNumber} />
      )
    case "quote":
      return <QuoteBlock block={block} />
    case "video":
      return <VideoBlock block={block} titleNumber={blockTitleNumber} />
    case "html":
      return <HtmlBlock block={block} />
    case "divider":
      return <DividerBlock block={block} titleNumber={blockTitleNumber} />
    case "full_image":
      return (
        <FullImageBlock
          block={block}
          fullImageNumber={fullImageNumber}
          titleNumber={blockTitleNumber}
        />
      )
    case "color_palette":
      return <ColorPaletteBlock block={block} titleNumber={blockTitleNumber} />
    case "carousel":
      return <CarouselBlock block={block} titleNumber={blockTitleNumber} />
    case "persona":
      return <PersonaBlock block={block} titleNumber={blockTitleNumber} />
    case "padding":
      return <HStack padding={`${block.amount}px`} />
    case "pros_and_cons":
      return <ProsAndConsBlock block={block} titleNumber={blockTitleNumber} />
    case "compare":
      return <CompareBlock block={block} titleNumber={blockTitleNumber} />
    case "donut_chart":
      return <DonutChart block={block} titleNumber={blockTitleNumber} />
    case "url":
      return <UrlBlock block={block} titleNumber={blockTitleNumber} />
    case "svg":
      return <SVGBlock block={block} titleNumber={blockTitleNumber} />
    case "figma":
      return <FigmaBlock block={block} titleNumber={blockTitleNumber} />
    case "mockup":
      return <MockupBlock block={block} titleNumber={blockTitleNumber} />
    case "color_palette_in_columns":
      return (
        <ColorPaletteColumnsBlock
          block={block}
          titleNumber={blockTitleNumber}
        />
      )
    default:
      console.warn(`Unknown content block type: ${(block as any).__typename}`)
      return null
  }
}

const ContentBlockRenderer: FC<ContentBlockRendererProps> = ({
  className,
  contentBlocks,
}) => {
  // console.log('ContentBlockRenderer render', contentBlocks);
  if (!contentBlocks || contentBlocks.length === 0) {
    return null
  }

  // Sort content blocks by their sort number from smaller to bigger
  const sortedContentBlocks = contentBlocks.slice().toSorted((a, b) => {
    const sortA = (a as any).sort || 0
    const sortB = (b as any).sort || 0
    return sortA - sortB
  })

  const numberedContentBlocks = sortedContentBlocks.reduce(
    (state: NumberedContentBlockState, block) => {
      const item = block.item
      const title = item ? getContentBlockTitle(item) : undefined
      const fullImageCount =
        item?.__typename === "full_image"
          ? state.fullImageCount + 1
          : state.fullImageCount
      const blockTitleCount = title
        ? state.blockTitleCount + 1
        : state.blockTitleCount

      return {
        blockTitleCount,
        fullImageCount,
        items: [
          ...state.items,
          {
            block,
            blockTitleNumber: title ? blockTitleCount : undefined,
            fullImageNumber:
              item?.__typename === "full_image" ? fullImageCount : undefined,
          },
        ],
      }
    },
    {
      blockTitleCount: 0,
      fullImageCount: 0,
      items: [],
    }
  ).items

  return (
    <VStack
      alignItems="flex-start"
      className={className}
      gap={0}
      mb={0}
      pb={0}
      width="100%"
    >
      {numberedContentBlocks.map(
        ({ block, blockTitleNumber, fullImageNumber }, index) => {
          if (!block.item) {
            return null
          }

          return (
            <Wrapper
              borderBottom={index === numberedContentBlocks.length - 1}
              className={`${block.item.__typename}-wrapper`}
              key={`${block.item.__typename}-${block.sort}`}
              showTopDivider={getShowTopDivider(block, index)}
            >
              <ContentBlock
                block={block.item}
                blockTitleNumber={blockTitleNumber}
                fullImageNumber={fullImageNumber}
              />
            </Wrapper>
          )
        }
      )}
    </VStack>
  )
}

export default ContentBlockRenderer
